/**
 * @file ArtifactWorkbench.js
 * @description Auto-maintained cleanup: comments & minor a11y/robustness. No UI/feature changes.
 */
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import './ArtifactWorkbench.css';

// Services

import { ImageAPI } from '../services/backend/imageService.backend';
import { createVideoJob, createAdvancedVideoJob, getVideoJob, submitTranscoderSmooth, getTranscoderJob } from '../services/backend/videoService.backend';
import { getVideoFrameThumb } from './videoThumb';
import * as creditService from '../services/creditService';

import * as metering from '../services/metering';
import { AudioAPI } from '../services/backend/audioService.backend';
import { validateImageContent } from '../services/safetyService.frontend';

const FEATURE_CREDITS_IMAGES = true;
const IMAGE_PRICE_CODE = 'image_1024';

const MODELS = {
    image: ['OpenAI (gpt-image-1)'],
    video: ['Google Veo 3.1'],
    audio: ['TTS'],
};

const defaultModelOptions = {
    image: [{ alias: 'gpt-image-1', label: 'GPT-Image-1 (default)', default: true }],
    video: [
        { alias: 'veo', label: 'Veo (Vertex)', default: true },
        { alias: 'kling-v2-6', label: 'Kling v2.6' }
    ],
    audio: [{ alias: 'lyria-002', label: 'Lyria-002', default: true }, { alias: 'tts-1', label: 'OpenAI TTS-1' }],
};

// Modality options (drives logic + replaces old tabs)
const MODALITIES = {
    image: ['text-to-image', 'image-to-image'],
    video: ['image-to-video', 'text-to-video'],
    audio: ['text-to-music', 'text-to-speech'],
};
const modalityLabel = (m) => ({
    'text-to-image': 'Text → Image',
    'image-to-image': 'Image → Image',
    'image-to-video': 'Image → Video',
    'text-to-video': 'Text → Video',
    'text-to-music': 'Text → Music',
    'text-to-speech': 'Text → Speech',
}[m] || m);


const isImgLike = (url = '') => /^data:image\//i.test(url) || /\.(png|jpg|jpeg|webp|gif)(\?|#|$)/i.test(url);
const isVideoLike = (url = '') => /^data:video\//i.test(url) || /\.(mp4|webm|mov|m4v)$/i.test(url);

const getThumb = (a, kind) => {

    if (kind === 'image') return a.outputUrl || a.imageUrl || a.url || a.inputImageUrl || a.coverImageURL || '';

    if (kind === 'video') {
        if (a.thumbnailUrl) return a.thumbnailUrl;
        if (isImgLike(a.outputUrl)) return a.outputUrl;
        return a.inputImageUrl || '/static/media/video-fallback.png';
    }

    return '';
};

// Convert raw b64 → data URL for preview
const toDataUrlIfBase64 = (val, mime = 'image/png') => {
    if (!val) return val;
    if (/^(data:|https?:|blob:)/i.test(val)) return val;
    if (/^[A-Za-z0-9+/=\s]+$/.test(val) && val.length > 100) {
        return `data:${mime};base64,${val.trim()}`;
    }
    return val;
};

// blob: object URL → data URL
async function objectUrlToDataUrl(objUrl) {
    const res = await fetch(objUrl);
    const blob = await res.blob();
    const reader = new FileReader();
    return await new Promise((resolve, reject) => {
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}


const FullScreenAudioPlayer = ({ src }) => {
    const audioRef = useRef(null);
    const [playing, setPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const [currentTime, setCurrentTime] = useState(0);

    const togglePlay = () => {
        if (!audioRef.current) return;
        if (playing) {
            audioRef.current.pause();
        } else {
            audioRef.current.play();
        }
        setPlaying(!playing);
    };

    const handleTimeUpdate = () => {
        if (!audioRef.current) return;
        const curr = audioRef.current.currentTime;
        const dur = audioRef.current.duration;
        setCurrentTime(curr);
        if (dur) setProgress((curr / dur) * 100);
    };

    const handleLoadedMetadata = () => {
        if (!audioRef.current) return;
        setDuration(audioRef.current.duration);
    };

    const handleSeek = (e) => {
        const val = e.target.value;
        if (!audioRef.current) return;
        const newTime = (val / 100) * duration;
        audioRef.current.currentTime = newTime;
        setProgress(val);
    };

    const formatTime = (s) => {
        if (!s) return '0:00';
        const m = Math.floor(s / 60);
        const sec = Math.floor(s % 60);
        return `${m}:${sec < 10 ? '0' : ''}${sec}`;
    };

    // Generate bars for visualizer
    const bars = useMemo(() => Array.from({ length: 40 }), []);

    return (
        <div className="aw-audio-fullscreen">
            <audio
                ref={audioRef}
                src={src}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onEnded={() => setPlaying(false)}
                crossOrigin="anonymous"
            />

            <div className={`aw-visualizer ${playing ? 'playing' : ''}`}>
                {bars.map((_, i) => (
                    <div key={i} className="aw-viz-bar" style={{ animationDelay: `${i * 0.05}s` }} />
                ))}
            </div>

            <div className="aw-audio-controls-overlay">
                <div className="aw-audio-row">
                    <button className="aw-audio-big-play" onClick={togglePlay}>
                        {playing ? (
                            <svg viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" /></svg>
                        ) : (
                            <svg viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3" /></svg>
                        )}
                    </button>
                </div>

                <div className="aw-audio-scrubber-row">
                    <span className="aw-audio-time">{formatTime(currentTime)}</span>
                    <input
                        type="range"
                        className="aw-audio-seek"
                        min="0" max="100"
                        value={progress}
                        onChange={handleSeek}
                    />
                    <span className="aw-audio-time">{formatTime(duration)}</span>
                </div>
            </div>
        </div>
    );
};

export default function ArtifactWorkbench({
    mode = 'image',
    prefill,
    quickMode = false,
    defaultProjectId = null,
    onBack,
    onSave,
    projectId,
    artifacts = { image: [], video: [], audio: [] },
    onRequestModeChange,
    modelOptions = defaultModelOptions,
    onDelete,
}) {

    const [libraryTab, setLibraryTab] = useState(mode);

    // Sync library tab when mode changes (optional mainly for initial open)
    useEffect(() => {
        if (mode) setLibraryTab(mode);
    }, [mode]);

    const [activeTab, setActiveTab] = useState(mode === 'image' ? 'text' : 'image'); // 'text' | 'image'
    const [modality, setModality] = useState(
        mode === 'image' ? 'text-to-image' : mode === 'video' ? 'image-to-video' : 'text-to-music'
    );

    const [prompt, setPrompt] = useState('');
    const makeIdem = useCallback(() => `img-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`, []);
    const [inputImages, setInputImages] = useState([{ id: makeIdem(), type: 'empty' }]); // Start with 1 empty slot
    const [title, setTitle] = useState('');
    const [isRunning, setIsRunning] = useState(false);
    const [progress, setProgress] = useState(0);
    const [resultUrl, setResultUrl] = useState('');
    const [error, setError] = useState('');
    const [duration, setDuration] = useState(5);
    // Removed overlayStyle state (moved to CSS flexbox)
    const [preview, setPreview] = useState(null);
    const [ledgerId, setLedgerId] = useState(null);
    const [reservedCost, setReservedCost] = useState(null);
    // Flex-box mode: always ready, no JS measure
    const [overlayReady, setOverlayReady] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [savedSig, setSavedSig] = useState(null);
    const [isDropOver, setIsDropOver] = useState(false);
    const [ctxMenu, setCtxMenu] = useState({ show: false, x: 0, y: 0, kind: null, artifact: null });
    const [showUrlInput, setShowUrlInput] = useState(false); // legacy/global toggle
    const [activeSlotId, setActiveSlotId] = useState(null); // Track which slot is requesting a file
    const activeSlotIdRef = React.useRef(null); // Ref to avoid staleness during file picker roundtrip
    const [urlInputVal, setUrlInputVal] = useState(''); // Temp state for URL input in a slot

    const currentSig = useMemo(() => (resultUrl ? resultUrl.slice(0, 64) : ''), [resultUrl]);
    const isSaved = !!savedSig && savedSig === currentSig;

    // Helper to read models for current modality
    const modelsForMode = (m) => {
        let list = [];
        if (m === 'image') list = (modelOptions?.image?.length ? modelOptions.image : defaultModelOptions.image);
        else if (m === 'video') list = (modelOptions?.video?.length ? modelOptions.video : defaultModelOptions.video);
        else list = (modelOptions?.audio?.length ? modelOptions.audio : defaultModelOptions.audio); // audio

        // 1. Filter by 'playground' tag
        list = list.filter(model => {
            if (Array.isArray(model.tags)) return model.tags.includes('playground');
            return true;
        });

        // 2. Filter by Capability (if present)
        // If we are currently in "image-to-image" mode, only show models that say they support it.
        // If a model has NO capabilities list, we assume it's legacy/supported (allow-all) to be safe, 
        // OR strict mode: we only allow if explicit.
        // Let's go with: if capabilities is defined and non-empty, we enforce it.
        if (m === 'image' && modality === 'image-to-image') {
            list = list.filter(model => {
                // If backend doesn't send capabilities, assume yes? Or specific checks?
                // Better to assume yes for now unless explicitly excluded.
                if (!model.capabilities || model.capabilities.length === 0) return true;
                return model.capabilities.includes('image-to-image');
            });
        }

        // NEW: Audio Filtering based on modality (Music vs Speech)
        if (m === 'audio') {
            if (modality === 'text-to-music') {
                // Heuristic: filter for Lyria or exclude TTS
                list = list.filter(x => x.alias === 'lyria-002');
            } else if (modality === 'text-to-speech') {
                // Heuristic: filter for TTS
                list = list.filter(x => x.alias === 'tts-1');
            }
        }

        return list;
    };

    const defaultAliasForMode = (m) => {
        const list = modelsForMode(m);
        return (list.find(x => x.default)?.alias) || (list[0]?.alias) || '';
    };

    //For backwards compatibility when you remove a model from the selection
    const normalizeToAlias = (m, value) => {
        const list = modelsForMode(m);
        if (!value) return defaultAliasForMode(m);
        // already an alias?
        if (list.some(x => x.alias === value)) return value;
        // try find alias inside a legacy label like "OpenAI (gpt-image-1)"
        const paren = String(value).match(/\(([^)]+)\)/);
        if (paren && list.some(x => x.alias === paren[1])) return paren[1];
        // or substring match
        const hit = list.find(x => String(value).includes(x.alias));
        if (hit) return hit.alias;
        return defaultAliasForMode(m);
    };

    // Store the **alias** as the selected model value
    const [model, setModel] = useState(defaultAliasForMode(mode));
    const [voice, setVoice] = useState('alloy'); // OpenAI TTS Voice

    // Determine strict max images for current model
    const currentModelConfig = modelsForMode(mode).find(x => x.alias === model);
    const maxImages = currentModelConfig?.maxInputImages || 4;  // Default to 4 if unspecified

    // Truncate inputs if model switches to one with lower limit
    useEffect(() => {
        if (inputImages.length > maxImages) {
            setInputImages(prev => prev.slice(0, maxImages));
        }
    }, [maxImages, inputImages.length]);


    useEffect(() => {
        document.body.classList.add('aw-no-x-scroll');
        return () => document.body.classList.remove('aw-no-x-scroll');
    }, []);

    useEffect(() => {
        setSavedSig(null);
        setIsSaving(false);
        setError('');
    }, [currentSig]);

    useEffect(() => {
        if (!prefill?.artifact) return;
        const { artifact, kind } = prefill;
        populateFromArtifact(artifact, kind);
    }, [prefill]);
    // PURE CSS/FLEX MODE: 
    // No JS calculation needed. The overlay is now position:relative/flex 
    // and sits naturally in the flex container next to the sidebar.
    const overlayStyle = {};

    // Reset on mode change
    useEffect(() => {
        const hasPrefillForThisMode = !!prefill?.artifact && (prefill?.kind === mode);
        if (hasPrefillForThisMode) return;   // keep the artifact’s model/modality
        setModel(defaultAliasForMode(mode));
        setActiveTab(mode === 'image' ? 'text' : 'image');
        setModality(mode === 'image' ? 'text-to-image' : mode === 'video' ? 'image-to-video' : 'text-to-music');
        setInputImages([{ id: makeIdem(), type: 'empty' }]); setPrompt(''); setTitle('');
        setIsRunning(false); setProgress(0); setResultUrl(''); setError('');
        setShowUrlInput(false); // Reset URL input toggle
    }, [mode, prefill?.artifact, prefill?.kind]);

    // Keep modality and activeTab in sync
    useEffect(() => {
        if (mode === 'image') setModality(activeTab === 'text' ? 'text-to-image' : 'image-to-image');
        if (mode === 'video') setModality(activeTab === 'image' ? 'image-to-video' : 'text-to-video');
    }, [activeTab, mode]);

    useEffect(() => {
        const onEsc = (e) => { if (e.key === 'Escape') setPreview(null); };
        window.addEventListener('keydown', onEsc);
        return () => window.removeEventListener('keydown', onEsc);
    }, []);

    const urlKind = (url = '') => {
        if (/^data:video\//i.test(url) || /\.(mp4|webm|mov|m4v)$/i.test(url)) return 'video';
        if (/^data:audio\//i.test(url) || /\.(mp3|wav|m4a|ogg)$/i.test(url)) return 'audio';
        return 'image';
    };

    const openPreview = (fallbackType, a) => {
        const url = a?.outputUrl || a?.inputImageUrl || '';
        const type = urlKind(url) || fallbackType || 'image';
        if (!url) return;
        setPreview({ type, url, title: a?.title || '' });
    };


    const validateSlotImage = async (id, input) => {
        try {
            let base64data = '';
            let mime = 'image/png';

            if (input instanceof Blob) {
                // File/Blob
                mime = input.type || 'image/png';
                const reader = new FileReader();
                reader.readAsDataURL(input);
                await new Promise(resolve => {
                    reader.onloadend = () => {
                        base64data = reader.result;
                        resolve();
                    };
                });
            } else if (typeof input === 'string' && input.startsWith('data:')) {
                // Data URI
                base64data = input;
                const m = input.match(/data:([^;]+);/);
                if (m) mime = m[1];
            } else if (typeof input === 'string' && (input.startsWith('http') || input.startsWith('//'))) {
                // Remote URL
                base64data = input; // Service handles detection
                mime = 'image/jpeg'; // Default assumption, backend can detect or ignored
            } else {
                return;
            }

            console.log("Validating image for slot", id, mime);
            const result = await validateImageContent(base64data, mime);

            setInputImages(prev => prev.map(item => {
                if (item.id !== id) return item;
                if (result.allowed) {
                    return { ...item, safety: { status: 'safe' } };
                } else {
                    return { ...item, safety: { status: 'unsafe', reason: result.reason } };
                }
            }));

        } catch (e) {
            console.error("Validation failed", e);
            setInputImages(prev => prev.map(item => {
                if (item.id !== id) return item;
                return { ...item, safety: { status: 'unsafe', reason: "Validation error" } };
            }));
        }
    };

    const addFile = (file, targetSlotId = null) => {
        if (!file) return;
        const url = URL.createObjectURL(file);
        // Init with scanning state
        const newItem = { id: makeIdem(), type: 'file', value: url, file, safety: { status: 'scanning' } };

        // Trigger background validation
        validateSlotImage(newItem.id, file);

        setInputImages(prev => {
            // If targeting a specific empty slot, replace it
            if (targetSlotId) {
                return prev.map(item => item.id === targetSlotId ? newItem : item);
            }
            // Otherwise, see if there's any empty slot to fill
            const firstEmpty = prev.find(x => x.type === 'empty');
            if (firstEmpty) {
                return prev.map(x => x.id === firstEmpty.id ? newItem : x);
            }
            // Logic: if we are at limit (4), replace the last one? or do nothing? 
            // User requirement: "keep introducing new image slots". Implies manual add.
            // If dragging, we should prob append if space?
            if (prev.length < maxImages) {
                return [...prev, newItem];
            }
            return prev; // Full
        });
        setActiveSlotId(null);
    };

    // For legacy compat or single-click upload
    const handleFile = (file) => {
        // Use ref for immediate access, fallback to state
        const target = activeSlotIdRef.current || activeSlotId;
        addFile(file, target);
        // Reset ref
        activeSlotIdRef.current = null;
    };

    const addUrl = (url, targetSlotId = null, skipSafety = false) => {
        if (!url) return;
        const id = makeIdem();
        // Check for data URI OR remote URL to validate
        // IF skipSafety is true, force shouldValidate to false
        const shouldValidate = !skipSafety && (url.startsWith('data:image') || url.startsWith('http') || url.startsWith('//'));

        const newItem = {
            id, type: 'url', value: url,
            safety: shouldValidate ? { status: 'scanning' } : { status: 'safe' } // explicit safe if skipped
        };

        if (shouldValidate) {
            validateSlotImage(id, url);
        }
        setInputImages(prev => {
            if (targetSlotId) {
                return prev.map(item => item.id === targetSlotId ? newItem : item);
            }
            const firstEmpty = prev.find(x => x.type === 'empty');
            if (firstEmpty) {
                return prev.map(x => x.id === firstEmpty.id ? newItem : x);
            }
            if (prev.length < maxImages) {
                return [...prev, newItem];
            }
            return prev;
        });
    };

    const addEmptySlot = () => {
        setInputImages(prev => {
            if (prev.length >= maxImages) return prev;
            return [...prev, { id: makeIdem(), type: 'empty' }];
        });
    };

    const updateInputImage = (id, partial) => {
        setInputImages(prev => prev.map(x => x.id === id ? { ...x, ...partial } : x));
    };

    const removeInput = (id) => {
        setInputImages(prev => {
            const item = prev.find(x => x.id === id);
            if (item?.type === 'file' && item.value?.startsWith('blob:')) {
                URL.revokeObjectURL(item.value);
            }
            // Don't remove the *last* slot if it matches initial state logic?
            // User says "keep introducing...". If I remove a filled slot, should it become empty or disappear?
            // "others disappear" bug suggests disappear is confusing.
            // Typically "remove" means delete.
            // But if it's the ONLY slot, it should revert to empty?
            if (prev.length === 1) {
                return [{ id: makeIdem(), type: 'empty' }];
            }
            return prev.filter(x => x.id !== id);
        });
    };

    // Ref to track known blobs to prevent premature cleanup
    const knownBlobsRef = useRef(new Set());

    // Sync blobs: Revoke only those that are no longer in state
    useEffect(() => {
        const nextBlobs = new Set();
        inputImages.forEach(img => {
            if (img.type === 'file' && img.value?.startsWith('blob:')) {
                nextBlobs.add(img.value);
            }
        });
        if (resultUrl?.startsWith('blob:')) nextBlobs.add(resultUrl);

        // Revoke any blob that was known but is no longer in the new set
        knownBlobsRef.current.forEach(url => {
            if (!nextBlobs.has(url)) {
                URL.revokeObjectURL(url);
            }
        });

        knownBlobsRef.current = nextBlobs;
    }, [inputImages, resultUrl]);

    // Final cleanup on unmount
    useEffect(() => {
        return () => {
            knownBlobsRef.current.forEach(url => URL.revokeObjectURL(url));
        };
    }, []);

    // Layout effect removed: overlay is now flex-positioned by CSS

    // close on any left click or Escape
    useEffect(() => {
        const close = () => setCtxMenu(m => ({ ...m, show: false }));
        const esc = (e) => { if (e.key === 'Escape') close(); };
        document.addEventListener('click', close);
        document.addEventListener('keydown', esc);
        return () => {
            document.removeEventListener('click', close);
            document.removeEventListener('keydown', esc);
        };
    }, []);


    const startDragFromLibrary = (kind, a) => (e) => {
        try {
            const url =
                (kind === 'image')
                    ? (a.outputUrl || a.imageUrl || a.url || a.inputImageUrl || a.coverImageURL || '')
                    : (a.thumbnailUrl || a.outputUrl || a.inputImageUrl || a.url || '');
            const payload = JSON.stringify({ kind, url, id: a._id || a.id || null, title: a.title || '' });
            e.dataTransfer.setData('application/x-alekhine-artifact', payload);
            if (url) e.dataTransfer.setData('text/uri-list', url);
            e.dataTransfer.effectAllowed = 'copy';
        } catch { }
    };

    const onDropAreaDragOver = (e) => {
        const dt = e.dataTransfer;
        if (!dt) return;
        const ok =
            (dt.types && (dt.types.includes('application/x-alekhine-artifact') || dt.types.includes('Files') || dt.types.includes('text/uri-list')));
        if (ok) {
            e.preventDefault();
            dt.dropEffect = 'copy';
            setIsDropOver(true);
        }
    };
    const onDropAreaDragLeave = () => setIsDropOver(false);
    const onDropAreaDrop = (e) => {
        e.preventDefault();
        setIsDropOver(false);
        const dt = e.dataTransfer;
        // Internal artifact payload
        try {
            const raw = dt.getData('application/x-alekhine-artifact');
            if (raw) {
                const { kind, url } = JSON.parse(raw);
                if (kind === 'image' && url) {
                    setActiveTab('image');
                    // setInputImages([{ id: makeIdem(), type: 'url', value: url }]);
                    addUrl(url, null, true); // Skip safety check for library items
                    setShowUrlInput(false);
                    return;
                }
            }
        } catch { }
        // File drop (optional nicety)
        const file = dt.files && dt.files[0];
        // Loosen check: if it has no type but is a file, or is image
        if (file) {
            setActiveTab('image');
            // If they drop a file, try to fill an empty slot or append
            // If drop is generic, we don't have a specific target ID, so pass null
            handleFile(file);
            setShowUrlInput(false);
            return;
        }
        // Fallback to URL text
        const maybeUrl = dt.getData('text/uri-list') || dt.getData('text/plain');
        if (maybeUrl) {
            setActiveTab('image');
            addUrl(maybeUrl, null); // Use addUrl helper to merge safely
            setShowUrlInput(false);
        }
    };



    const onArtifactContext = (e, kind, a) => {
        e.preventDefault();
        const MENU_W = 180;
        const MENU_H = 44;
        const PAD = 8;

        // Viewport coords
        let x = e.clientX;
        let y = e.clientY;

        // Keep within viewport
        x = Math.min(x, window.innerWidth - MENU_W - PAD);
        y = Math.min(y, window.innerHeight - MENU_H - PAD);

        setCtxMenu({ show: true, x, y, kind, artifact: a });
    };

    const handleDownloadFromMenu = async () => {
        const a = ctxMenu.artifact;
        const kind = ctxMenu.kind;
        setCtxMenu(m => ({ ...m, show: false }));

        if (!a) return;
        const url = a.outputUrl || a.imageUrl || a.videoUrl || a.url || '';
        if (!url) return;

        // Construct a filename
        const cleanTitle = (a.title || 'artifact').replace(/[^a-z0-9_-]/gi, '_');
        const ext = kind === 'image' ? 'png' : kind === 'video' ? 'mp4' : 'mp3';
        const filename = `${cleanTitle}.${ext}`;

        try {
            // Attempt to fetch blob to force download (works for cross-origin if CORS allows)
            const response = await fetch(url);
            if (!response.ok) throw new Error('Network response was not ok');
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up
            setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
        } catch (e) {
            console.warn('Direct download failed (likely CORS), falling back to new tab:', e);
            // Fallback: Open in new window
            window.open(url, '_blank');
        }
    };

    const handleDeleteFromMenu = async () => {
        const a = ctxMenu.artifact;
        const kind = ctxMenu.kind;
        setCtxMenu(m => ({ ...m, show: false }));
        if (!a || !kind) return;
        // prefer parent so the library updates globally
        if (typeof onDelete === 'function') {
            await onDelete(a, kind, projectId || defaultProjectId || null);
        }
    };


    const clearInputs = () => {
        setInputImages(prev => {
            prev.forEach(img => {
                if (img.type === 'file' && img.value?.startsWith('blob:')) {
                    URL.revokeObjectURL(img.value);
                }
            });
            // Reset to 1 empty slot so UI doesn't collapse
            return [{ id: makeIdem(), type: 'empty' }];
        });
        setTitle('');
        setPrompt('');
        setResultUrl('');
        setError('');
        setProgress(0);
        setPreview?.(null); // if lightbox is open, close
        // setOverlayReady(false); // FIXED: Do not hide the workbench on clear!
        setLedgerId(null);
        setReservedCost(null);
        setSavedSig(null);
    };

    const runGeneration = async () => {
        setIsRunning(true); setError(''); setProgress(0); setResultUrl('');
        const tick = setInterval(() => setProgress(p => Math.min(95, p + Math.random() * 12)), 420);

        let localLedgerId = null; // track reservation only when the feature flag is on

        try {
            //Run the generation (existing behavior)
            let url;

            if (mode === 'image') {
                const pid = projectId || defaultProjectId || 'quick-image';
                const sceneNumber = prefill?.artifact?.sceneNumber ?? 'playground';

                const runCore = async () => {
                    let out;
                    // Filter out empty slots
                    const validImages = inputImages.filter(x => x.type !== 'empty');

                    if (modality === 'image-to-image' && validImages.length > 0) {
                        const processedImages = await Promise.all(validImages.map(async (img) => {
                            if (img.type === 'file' && img.value.startsWith('blob:')) {
                                return await objectUrlToDataUrl(img.value);
                            }
                            return img.value;
                        }));

                        // NEW: Use the dedicated template approach
                        out = await ImageAPI.artifactWithRefs({
                            prompt,
                            model,
                            images: processedImages
                        });
                    } else {
                        out = await ImageAPI.raw({ prompt, model });
                    }
                    return toDataUrlIfBase64(out, 'image/png');
                };

                url = await metering.runArtifactImage(
                    { projectId: pid, meta: { model, modality } },
                    runCore
                );
            } else if (mode === 'video') {
                const poster = (modality === 'image-to-video' && inputImages.length > 0)
                    ? (inputImages[0].type === 'file' ? await objectUrlToDataUrl(inputImages[0].value) : inputImages[0].value)
                    : undefined;

                // Use metering for Artifact Workbench video generation
                const pid = projectId || defaultProjectId || 'quick-video';
                const sceneNumber = prefill?.artifact?.sceneNumber ?? 'playground';

                const runCore = async () => {
                    let jobId;

                    // NEW: Check for multi-image input (Advanced / Veo strategy)
                    const validImages = inputImages.filter(x => x.type !== 'empty');
                    if (modality === 'image-to-video' && validImages.length > 1) {
                        // Convert all images to processed format (b64/url)
                        const processedRefs = await Promise.all(validImages.map(async (img) => {
                            if (img.type === 'file' && img.value.startsWith('blob:')) {
                                // Convert blobs to base64 data URI
                                const dUri = await objectUrlToDataUrl(img.value);
                                // If Veo expects raw base64 in "bytesBase64Encoded", strip prefix? 
                                // Veo provider handles "b64" key, let's pass a structured object.
                                // Actually let's assume the provider can handle data URIs if we pass them as "b64" or "gcsUri"
                                // Looking at veo.py: "b64 = ri.get('b64')" ... 
                                // It's cleaner to pass the dataURI and let the backend strip it if needed, or strip it here.
                                // veo.py _jpeg_url_to_b64 returns raw b64.
                                // Let's safe-strip here.
                                const b64 = dUri.split(',')[1];
                                return { b64, mimeType: 'image/jpeg' };
                            }
                            // String URL
                            return { gcsUri: img.value, mimeType: 'image/jpeg' };
                        }));

                        jobId = await createAdvancedVideoJob(prompt, processedRefs, { duration }, model);

                    } else {
                        // Legacy single-image flow
                        jobId = await createVideoJob(prompt, poster, { duration }, model);
                    }
                    // 2) Poll job
                    let lastProgress = 5;
                    for (; ;) {
                        const job = await getVideoJob(jobId); // { state, progress, url, error, ... }
                        if (job?.progress != null) {
                            setProgress(Math.min(95, Math.max(lastProgress, job.progress)));
                            lastProgress = job.progress;
                        }
                        if (job?.state === 'SUCCEEDED' && job?.url) return job.url;
                        if (job?.state === 'FAILED') throw new Error(job?.error || 'Video job failed');
                        await new Promise(r => setTimeout(r, 4000)); // ~4s poll
                    }
                };

                // Reserve → run → finalize (or revert on failure)
                url = await metering.runArtifactVideo(
                    {
                        projectId: pid,
                        durationSec: Math.ceil(Number(duration || 5)),
                        meta: { model, modality }
                    },
                    runCore
                );

                // --- VEO SMOOTHING INTEGRATION ---
                // If model is Veo, automatically run smoothing (VFR -> CFR 24fps)
                if (url && (String(model).toLowerCase().includes('veo'))) {
                    try {
                        console.log("Veo model detected, initiating auto-smoothing...");
                        // We can't update text-based progress easily without a specific UI state, 
                        // but we can keep the progress bar busy.
                        const smoothName = `smoothed_artifact_${Date.now()}`;
                        const { jobId: smoothJobId, outputUrl: smoothUrl } = await submitTranscoderSmooth(url, smoothName);

                        // Poll
                        let smoothState = 'PENDING';
                        for (let k = 0; k < 60; k++) { // ~4 mins max
                            const j = await getTranscoderJob(smoothJobId);
                            smoothState = j?.state || smoothState;
                            if (smoothState === 'SUCCEEDED') {
                                url = smoothUrl; // Swap result
                                console.log("Auto-smoothing succeeded:", url);
                                break;
                            }
                            if (smoothState === 'FAILED') {
                                console.warn("Auto-smoothing failed:", j);
                                break;
                            }
                            await new Promise(r => setTimeout(r, 4000));
                        }
                    } catch (e) {
                        console.warn("Auto-smoothing error (falling back to raw):", e);
                    }
                }
                // ---------------------------------
            } else if (mode === 'audio') {
                const pid = projectId || defaultProjectId || 'quick-audio';
                if (modality === 'text-to-speech') {
                    url = await metering.runTTS({ projectId: pid, text: prompt, meta: { voice, model } }, async () => {
                        return await AudioAPI.generateSpeech({
                            text: prompt,
                            voice,
                            model
                        });
                    });
                } else {
                    // text-to-music
                    url = await metering.runMusic({ projectId: pid, meta: { modality } }, async () => {
                        return await AudioAPI.generate({
                            prompt,
                            negative_prompt: '',
                            seed: undefined
                        });
                    });
                }
            }

            if (!url) throw new Error('No URL returned from the generator service.');
            setResultUrl(url);
            setProgress(100);

            // 3) Finalize reservation (no-op while flag is false)
            if (FEATURE_CREDITS_IMAGES && mode === 'image' && localLedgerId) {
                try { await creditService.finalize(localLedgerId, undefined); } catch { }
            }
        } catch (e) {
            if (process.env.NODE_ENV !== 'production') console.error(e);
            setError(e?.message || 'Generation failed.');
            // Revert reservation on failure (no-op while flag is false)
            if (FEATURE_CREDITS_IMAGES && mode === 'image' && localLedgerId) {
                try { await creditService.revert(localLedgerId, 'generation_failed'); } catch { }
                setLedgerId(null);
                setReservedCost(null);
            }
        } finally {
            clearInterval(tick);
            setTimeout(() => setIsRunning(false), 250);
        }
    };


    const titleOk = title.trim().length > 0;
    const canRun = useMemo(() => {
        if (!titleOk) return false;

        // Safety Check: Block if any image is scanning or unsafe
        const hasUnsafeOrScanning = inputImages.some(x => x.safety && (x.safety.status === 'scanning' || x.safety.status === 'unsafe'));
        if (hasUnsafeOrScanning) return false;

        const hasImages = inputImages.some(x => x.type !== 'empty');
        if (mode === 'image') return modality === 'text-to-image' ? !!prompt.trim() : hasImages;
        if (mode === 'video') return modality === 'image-to-video' ? hasImages : !!prompt.trim();
        if (mode === 'audio') return !!prompt.trim();
        return false;
    }, [titleOk, mode, modality, prompt, inputImages]);

    // Shortcuts
    useEffect(() => {
        const onKey = (e) => {
            const mod = e.metaKey || e.ctrlKey;
            if (mod && e.key === 'Enter') { e.preventDefault(); if (canRun && !isRunning) runGeneration(); }
            if (mod && (e.key.toLowerCase() === 's')) {
                if (!resultUrl || !title || isRunning) return;
                e.preventDefault();
                attemptSave();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [canRun, isRunning, resultUrl, title, mode, modality, inputImages, prompt, model]);

    const populateFromArtifact = (a, kind) => {
        // If artifact type doesn't match current mode, ask parent to switch (if provided)
        if (kind !== mode) {
            if (typeof onRequestModeChange === 'function') {
                onRequestModeChange({ mode: kind, artifact: a });
            }
            return;
        }

        // Title / prompt / model
        setTitle(a.title || '');
        setPrompt(a.prompt || '');
        //if (a.model) setModel(a.model);
        if (a.model) setModel(normalizeToAlias(kind, a.model));

        // Input modality + inputs
        if (kind === 'image') {
            const isI2I = !!a.inputImageUrl || (Array.isArray(a.inputImages) && a.inputImages.some(v => v));
            setModality(isI2I ? 'image-to-image' : 'text-to-image');
            setActiveTab(isI2I ? 'image' : 'text'); // Sync activeTab
            if (isI2I) {
                if (Array.isArray(a.inputImages)) {
                    // Filter out nulls/empty strings
                    const valid = a.inputImages.filter(v => v);
                    if (valid.length) {
                        setInputImages(valid.map(url => ({ id: makeIdem(), type: 'url', value: url })));
                    } else {
                        // Fallback if all were empty
                        setInputImages([{ id: makeIdem(), type: 'url', value: a.inputImageUrl || '' }]);
                    }
                } else if (a.inputImageUrl) {
                    setInputImages([{ id: makeIdem(), type: 'url', value: a.inputImageUrl }]);
                }
            } else {
                setInputImages([{ id: makeIdem(), type: 'empty' }]);
            }
            setShowUrlInput(!!a.inputImageUrl && !a.inputImageUrl.startsWith('data:'));
        } else if (kind === 'video') {
            // Video/Audio checks remain comparable
            const isImg2Vid = !!a.inputImageUrl || (Array.isArray(a.inputImages) && a.inputImages.some(v => v));
            setModality(isImg2Vid ? 'image-to-video' : 'text-to-video');
            setActiveTab(isImg2Vid ? 'image' : 'text');
            setDuration(typeof a.duration === 'number' ? a.duration : duration);
            if (isImg2Vid) {
                if (Array.isArray(a.inputImages) && a.inputImages.length > 0) {
                    const valid = a.inputImages.filter(v => v);
                    if (valid.length) {
                        setInputImages(valid.map(url => ({ id: makeIdem(), type: 'url', value: url })));
                    } else {
                        setInputImages([{ id: makeIdem(), type: 'url', value: a.inputImageUrl || '' }]);
                    }
                } else if (a.inputImageUrl) {
                    setInputImages([{ id: makeIdem(), type: 'url', value: a.inputImageUrl }]);
                } else {
                    setInputImages([{ id: makeIdem(), type: 'empty' }]);
                }
            } else {
                setInputImages([{ id: makeIdem(), type: 'empty' }]);
            }
            setShowUrlInput(!!a.inputImageUrl && !a.inputImageUrl.startsWith('data:'));
        } else if (kind === 'audio') {
            // nothing special beyond prompt/title/model for now
        }

        // Show output in the Result panel
        const mime = kind === 'image' ? 'image/png' : kind === 'video' ? 'video/mp4' : 'audio/mpeg';
        const out = toDataUrlIfBase64(a.outputUrl, mime);
        setResultUrl(out || '');
        setError('');
        setProgress(100);
        setIsRunning(false);
    };

    const attemptSave = async () => {
        if (!resultUrl || isRunning || isSaving || isSaved) return;

        setError('');
        setIsSaving(true);
        try {

            // If saving a video, grab a frame; fall back to poster if needed
            let thumbnailUrl;
            if (mode === 'video') {
                try {
                    thumbnailUrl = await getVideoFrameThumb(resultUrl, 1.0);
                } catch { }
                if (!thumbnailUrl && inputImages.length > 0) {
                    thumbnailUrl = inputImages[0].value;
                }
            }

            // Filter inputs to valid slots only
            const validInputs = inputImages.filter(x => x.type !== 'empty' && x.value);

            // CONVERT BLOBS TO DATA URIS
            // The backend needs persistent data, not ephemeral blob: URLs
            const persistentInputs = await Promise.all(validInputs.map(async (img) => {
                if (img.value && img.value.startsWith('blob:')) {
                    try {
                        const r = await fetch(img.value);
                        const b = await r.blob();
                        return new Promise((resolve) => {
                            const reader = new FileReader();
                            reader.onloadend = () => resolve(reader.result); // a data: url
                            reader.readAsDataURL(b);
                        });
                    } catch (e) {
                        console.warn('Failed to convert blob to data uri', e);
                        return img.value;
                    }
                }
                return img.value;
            }));

            // Use the first converted input as the primary inputImageUrl
            const primaryInputUrl = persistentInputs.length > 0 ? persistentInputs[0] : '';
            // If primary input was a blob and we converted it, use that for thumbnail fallback too
            if (mode === 'video' && !thumbnailUrl && primaryInputUrl) {
                thumbnailUrl = primaryInputUrl;
            }

            const payload = {
                type: mode,
                title: title.trim(),
                prompt: prompt.trim(),
                inputImageUrl: (mode !== 'audio' && validInputs.length > 0) ? primaryInputUrl : undefined,
                inputImages: (mode !== 'audio' && validInputs.length > 0) ? persistentInputs : undefined,
                outputUrl: resultUrl,
                model,
                duration: mode === 'video' ? duration : undefined,
                createdAt: new Date().toISOString(),
                ...(thumbnailUrl ? { thumbnailUrl } : {}),
                ...(ledgerId && reservedCost > 0 ? { ledgerId, creditsDebited: reservedCost } : {})
            };

            // onSave may be sync or async; await if it returns a Promise
            const maybe = onSave && onSave(payload);
            if (maybe && typeof maybe.then === 'function') await maybe;

            // mark this specific result as saved (disables the button until result changes)
            setError('');
            setSavedSig(currentSig);
        } catch (e) {
            // optional: surface an error
            setError(e?.message || 'Save failed.');
        } finally {
            setIsSaving(false);
        }
    };

    const loadingLib = (artifacts === null);           // null => "Loading…"
    const imgs = loadingLib ? [] : (artifacts?.image || []);
    const vids = loadingLib ? [] : (artifacts?.video || []);
    const auds = loadingLib ? [] : (artifacts?.audio || []);

    const makeKey = (a, i) => a?._id || a?.id || a?.createdAt || a?.outputUrl || a?.inputImageUrl || `${a?.title || 'item'}-${i}`;

    const [vidThumbCache, setVidThumbCache] = useState({});

    useEffect(() => {
        let cancelled = false;
        (async () => {
            for (const a of vids) {
                if (a?.thumbnailUrl) continue;
                const url = a?.outputUrl || a?.videoUrl || a?.url;
                if (!url || vidThumbCache[url]) continue;

                const thumb = await getVideoFrameThumb(url, 1.0);
                if (!cancelled && thumb) {
                    setVidThumbCache(prev => (prev[url] ? prev : { ...prev, [url]: thumb }));
                }
            }
        })();
        return () => { cancelled = true; };
    }, [vids, vidThumbCache]);


    return (
        <div className={`aw-overlay ${overlayReady ? 'is-ready' : 'is-hidden'}`}>
            <div className="aw-root">
                {/* Top bar */}
                <div className="aw-topbar">
                    <div className="aw-leftside">
                        <button className="aw-back" onClick={onBack}>← Back</button>
                        <div className="aw-crumbs" />
                    </div>

                    {/* NEW: top-right actions */}
                    <div className="aw-rightside">
                        <button
                            className="aw-clear"
                            onClick={clearInputs}
                            disabled={isRunning}
                            title="Clear Title, Prompt, Image inputs, and Preview"
                        >
                            Clear
                        </button>
                    </div>
                </div>

                {/* Top half: Input | Result */}
                <div className="aw-top">
                    <div className="aw-panel aw-input">
                        <div className="aw-header">
                            {mode === 'image' ? 'Image Generation' : mode === 'video' ? 'Video Generation' : 'Audio Generation'}
                        </div>

                        {/* NEW: Scroll wrapper for everything except footer */}
                        <div className="aw-scroll-area">
                            {/* NEW: two-column row: Title | (Model + Modality) */}
                            <div className="aw-row">
                                <div className="aw-col">
                                    <div style={{ display: 'flex', gap: '1vh', width: '100%' }}>
                                        <div className="aw-field aw-title-field" style={{ width: '40%' }}>
                                            <label>Title</label>
                                            <input
                                                type="text"
                                                placeholder="Artifact title"
                                                value={title}
                                                onChange={(e) => setTitle(e.target.value)}
                                            />
                                        </div>
                                        {mode === 'video' && (
                                            <div className="aw-field" style={{ width: '12vh', flexShrink: 0 }}>
                                                <label style={{ whiteSpace: 'nowrap' }}>Duration (s)</label>
                                                <input
                                                    type="number"
                                                    min={1} max={30}
                                                    value={duration}
                                                    onChange={(e) => setDuration(parseInt(e.target.value || '5', 10))}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="aw-col aw-right-controls">
                                    <div className="aw-mini-field">
                                        <label>Modality</label>
                                        <select
                                            className="aw-compact-select"
                                            value={modality}
                                            onChange={(e) => {
                                                const m = e.target.value;
                                                setModality(m);
                                                if (mode === 'image') setActiveTab(m === 'text-to-image' ? 'text' : 'image');
                                                if (mode === 'video') setActiveTab(m === 'image-to-video' ? 'image' : 'text');
                                            }}
                                        >
                                            {MODALITIES[mode].map((m) => (
                                                <option key={m} value={m}>{modalityLabel(m)}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="aw-mini-field">
                                        <label>Model</label>
                                        <select
                                            className="aw-compact-select"
                                            value={model}
                                            onChange={(e) => setModel(e.target.value)}
                                        >
                                            {modelsForMode(mode).map(m => (
                                                <option key={m.alias} value={m.alias}>{m.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                            </div>

                            {/* Prompt or Text */}
                            <div className="aw-field aw-prompt">
                                <label>{modality === 'text-to-speech' ? 'Text' : 'Prompt'}</label>
                                <textarea
                                    placeholder={modality === 'text-to-speech' ? "Enter text to speak..." : "Describe your asset..."}
                                    value={prompt}
                                    onChange={(e) => setPrompt(e.target.value)}
                                />
                            </div>

                            {/* TTS Voice Selector */}
                            {mode === 'audio' && modality === 'text-to-speech' && (
                                <div className="aw-field">
                                    <label>Voice</label>
                                    <select
                                        className="aw-compact-select"
                                        style={{ width: '100%', marginTop: '0.8vh' }}
                                        value={voice}
                                        onChange={(e) => setVoice(e.target.value)}
                                    >
                                        <option value="alloy">Alloy (Neutral)</option>
                                        <option value="echo">Echo (Male)</option>
                                        <option value="fable">Fable (British)</option>
                                        <option value="onyx">Onyx (Deep Male)</option>
                                        <option value="nova">Nova (Female)</option>
                                        <option value="shimmer">Shimmer (Female)</option>
                                    </select>
                                </div>
                            )}

                            {/* Image input fields when needed */}
                            {mode !== 'audio' && ((mode === 'image' && modality === 'image-to-image') || (mode === 'video' && modality === 'image-to-video')) && (
                                <div className="aw-field">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.8vh', marginBottom: 0 }}>
                                        <label className="aw-label-spaced" style={{ marginBottom: 0 }}>Input Images</label>
                                        {inputImages.length < maxImages && (
                                            <span
                                                className="aw-add-input-btn"
                                                title="Add input image slot"
                                                style={{ color: '#0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                                                role="button"
                                                onClick={addEmptySlot}
                                            >
                                                +
                                            </span>
                                        )}
                                    </div>
                                    <div
                                        className={`aw-modern-dropzone ${isDropOver ? 'is-over' : ''} ${inputImages.length > 0 ? 'has-media' : ''}`}
                                        onDragOver={onDropAreaDragOver}
                                        onDragEnter={onDropAreaDragOver}
                                        onDragLeave={onDropAreaDragLeave}
                                        onDrop={onDropAreaDrop}
                                        onClick={() => {
                                            // If empty state (shouldn't happen often now), trigger generic upload
                                            if (inputImages.length === 0) {
                                                setActiveSlotId(null);
                                                document.getElementById('aw-hidden-file-input')?.click();
                                            }
                                        }}
                                    >
                                        <div className={`aw-image-list ${inputImages.length === 1 ? 'single-item' : ''}`}>
                                            {inputImages.map((img, i) => {
                                                if (img.type === 'empty') {
                                                    return (
                                                        <div className="aw-image-item empty-slot" key={img.id}>
                                                            <div className="aw-slot-content">
                                                                <div className="aw-slot-icon" onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setActiveSlotId(img.id);
                                                                    activeSlotIdRef.current = img.id; // Set ref immediately
                                                                    document.getElementById('aw-hidden-file-input')?.click();
                                                                }}>
                                                                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                                                        <polyline points="17 8 12 3 7 8" />
                                                                        <line x1="12" y1="3" x2="12" y2="15" />
                                                                    </svg>
                                                                </div>
                                                                <div className="aw-slot-text">or paste URL</div>
                                                            </div>

                                                            <input
                                                                className="aw-slot-url-input"
                                                                type="text"
                                                                placeholder="https://..."
                                                                onClick={(e) => e.stopPropagation()}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter' && e.currentTarget.value.trim()) {
                                                                        e.preventDefault();
                                                                        addUrl(e.currentTarget.value.trim(), img.id);
                                                                    }
                                                                }}
                                                            />
                                                            <button className="aw-item-remove" style={{ top: '0.4vh', right: '0.4vh' }} onClick={(e) => { e.stopPropagation(); removeInput(img.id); }}>✕</button>
                                                        </div>
                                                    );
                                                }
                                                return (
                                                    <div className="aw-image-item" key={img.id} style={{ border: 'none', background: 'transparent' }}>
                                                        <div className="aw-filled-slot">
                                                            <img
                                                                src={img.value}
                                                                alt={`input ${i}`}
                                                                style={img.safety?.status === 'unsafe' ? { filter: 'blur(10px) grayscale(1)' } : {}}
                                                                onLoad={(e) => {
                                                                    if (!img.width) {
                                                                        updateInputImage(img.id, { width: e.target.naturalWidth, height: e.target.naturalHeight });
                                                                    }
                                                                }}
                                                            />
                                                            {/* Safety Overlays */}
                                                            {img.safety?.status === 'scanning' && (
                                                                <div className="aw-safety-overlay scanning">
                                                                    <div className="aw-safety-loader"></div>
                                                                    <span>safety check</span>
                                                                </div>
                                                            )}
                                                            {img.safety?.status === 'unsafe' && (
                                                                <div className="aw-safety-overlay unsafe" title={`Reason: ${img.safety.reason || 'Content blocked'}`}>
                                                                    <div className="aw-safety-icon">
                                                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                                                            <circle cx="12" cy="12" r="10"></circle>
                                                                            <line x1="12" y1="8" x2="12" y2="12"></line>
                                                                            <line x1="12" y1="16" x2="12.01" y2="16"></line>
                                                                        </svg>
                                                                    </div>
                                                                    <span>safety check failed</span>
                                                                    <button
                                                                        className="aw-safety-retry"
                                                                        title="Retry safety check"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            updateInputImage(img.id, { safety: { status: 'scanning' } });
                                                                            validateSlotImage(img.id, img.file || img.value);
                                                                        }}
                                                                    >
                                                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                            <path d="M23 4v6h-6"></path>
                                                                            <path d="M1 20v-6h6"></path>
                                                                            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 1 3.51 15"></path>
                                                                        </svg>
                                                                    </button>
                                                                </div>
                                                            )}
                                                            <div className="aw-slot-overlay">
                                                                <div className="aw-overlay-top">
                                                                    <div className="aw-slot-btn" title="Remove" onClick={(e) => { e.stopPropagation(); removeInput(img.id); }}>
                                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" /></svg>
                                                                    </div>
                                                                </div>
                                                                <div className="aw-overlay-bottom">
                                                                    {img.width ? <div className="aw-res-tag">{img.width}x{img.height}</div> : <div />}
                                                                    <div className="aw-slot-btn" title="Open Link" onClick={(e) => { e.stopPropagation(); window.open(img.value, '_blank'); }}>
                                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71" /></svg>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div> {/* End aw-scroll-area */}



                        <div className="aw-actions">
                            <button className="aw-run" disabled={!canRun || isRunning} onClick={runGeneration}>
                                {isRunning ? 'Generating…' : 'Generate'}
                            </button>
                            <div className="aw-flex" />
                        </div>

                        {error && <div className="aw-error">{error}</div>}
                    </div>

                    <div className="aw-panel aw-result">
                        <div className="aw-header">
                            <span>Result</span>
                            {isRunning && <div className="aw-progress"><div className="aw-bar" style={{ width: `${progress}%` }} /></div>}
                        </div>
                        <div className="aw-stage">
                            {isRunning ? (
                                <div className="aw-loading-container">
                                    <img
                                        src={`${process.env.PUBLIC_URL}/static/media/loading1.eb6327c2b020fd151d29.gif`}
                                        alt="Loading..."
                                        className="aw-loading-animation"
                                    />
                                    <div>Generating {mode}, please wait...</div>
                                </div>
                            ) : (
                                <>
                                    {!resultUrl && <div className="aw-placeholder">Run to preview the result here.</div>}
                                    {mode === 'image' && !!resultUrl && <img className="aw-preview" src={resultUrl} alt="result" />}
                                    {mode === 'video' && !!resultUrl && (
                                        <video className="aw-preview" controls playsInline src={resultUrl} />
                                    )}
                                    {mode === 'audio' && !!resultUrl && <FullScreenAudioPlayer key={resultUrl} src={resultUrl} />}
                                </>
                            )}
                        </div>
                        <div className="aw-result-actions">
                            <button
                                className="aw-save"
                                disabled={!resultUrl || isRunning || isSaving || isSaved || !titleOk}
                                onClick={attemptSave}
                            >
                                {isSaving ? 'Saving…' : isSaved ? 'Saved' : 'Save'}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Bottom half: artifacts dock */}
                <div className="aw-bottom">
                    <div className="aw-dock">
                        <div className="aw-dock-header">
                            <div className="aw-tabs">
                                <button className={`aw-tab ${libraryTab === 'image' ? 'active' : ''}`} onClick={() => setLibraryTab('image')}>Images</button>
                                <button className={`aw-tab ${libraryTab === 'video' ? 'active' : ''}`} onClick={() => setLibraryTab('video')}>Videos</button>
                                <button className={`aw-tab ${libraryTab === 'audio' ? 'active' : ''}`} onClick={() => setLibraryTab('audio')}>Audio</button>
                            </div>
                        </div>
                        <div className="aw-dock-body">
                            {libraryTab === 'image' && (
                                <div className="aw-grid">
                                    {imgs.map((a, i) => {
                                        const t = getThumb(a, 'image');
                                        return (
                                            <a
                                                key={makeKey(a, i)}
                                                href={(a.outputUrl || a.inputImageUrl) || '#'}
                                                className="aw-chip"
                                                onClick={(e) => { e.preventDefault(); populateFromArtifact(a, 'image'); }}
                                                onContextMenu={(e) => onArtifactContext(e, 'image', a)}
                                                draggable
                                                onDragStart={startDragFromLibrary('image', a)}
                                                rel="noreferrer"
                                            >
                                                <div className="aw-chip-thumb">{t ? <img src={t} alt={a.title} /> : <span>IMG</span>}</div>
                                                <div className="aw-chip-title">{a.title || 'Untitled'}</div>
                                            </a>
                                        );
                                    })}
                                    {imgs.length === 0 && <div className="aw-empty">No images yet.</div>}
                                </div>
                            )}

                            {libraryTab === 'video' && (
                                <div className="aw-grid">
                                    {vids.map((a, i) => {
                                        const k = a?.outputUrl || a?.videoUrl || a?.url;
                                        const t = (k && vidThumbCache[k]) ? vidThumbCache[k] : getThumb(a, 'video');
                                        return (
                                            <a
                                                key={makeKey(a, i)}
                                                href={(a.outputUrl || a.inputImageUrl) || '#'}
                                                className="aw-chip"
                                                onClick={(e) => { e.preventDefault(); populateFromArtifact(a, 'video'); }}
                                                onContextMenu={(e) => onArtifactContext(e, 'video', a)}
                                                draggable
                                                onDragStart={startDragFromLibrary('video', a)}
                                                rel="noreferrer"
                                            >
                                                <div className="aw-chip-thumb">{t ? <img src={t} alt={a.title} /> : <span>VID</span>}</div>
                                                <div className="aw-chip-title">{a.title || 'Untitled'}</div>
                                            </a>
                                        );
                                    })}
                                    {vids.length === 0 && <div className="aw-empty">No videos yet.</div>}
                                </div>
                            )}

                            {libraryTab === 'audio' && (
                                <div className="aw-grid">
                                    {auds.map((a, i) => {
                                        // Determine if it's speech or music
                                        // Artifacts from TTS have .model='tts-1' etc.
                                        // Music artifacts have 'lyria-002'
                                        const isSpeech = (a.model && a.model.includes('tts')) || (a.modality === 'text-to-speech');
                                        return (
                                            <a
                                                key={makeKey(a, i)}
                                                href={a.outputUrl || '#'}
                                                className="aw-chip"
                                                onClick={(e) => { e.preventDefault(); populateFromArtifact(a, 'audio'); }}
                                                onContextMenu={(e) => onArtifactContext(e, 'audio', a)}
                                                draggable
                                                onDragStart={startDragFromLibrary('audio', a)}
                                                rel="noreferrer"
                                            >
                                                <div className="aw-chip-thumb aw-audio">
                                                    {isSpeech ? (
                                                        <img
                                                            src={`${process.env.PUBLIC_URL}/speech-icon.png`}
                                                            alt="Speech"
                                                            style={{ width: '25%', height: '25%', objectFit: 'contain', filter: 'invert(1)', mixBlendMode: 'screen', clipPath: 'inset(0 8% 0 0)' }}
                                                        />
                                                    ) : (
                                                        <span style={{ fontSize: '4vh' }}>♫</span>
                                                    )}
                                                </div>
                                                <div className="aw-chip-title">{a.title || 'Untitled'}</div>
                                            </a>
                                        )
                                    })}
                                    {auds.length === 0 && <div className="aw-empty">No audio yet.</div>}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {
                preview && (
                    <div className="aw-lightbox" onClick={() => setPreview(null)}>
                        <div className="aw-lightbox-panel" onClick={(e) => e.stopPropagation()}>
                            <div className="aw-lightbox-title">
                                <span>{preview.title || 'Preview'}</span>
                                <button className="aw-lightbox-close" onClick={() => setPreview(null)} aria-label="Close">×</button>
                            </div>

                            <div className="aw-lightbox-media">
                                {preview.type === 'image' && <img src={preview.url} alt={preview.title || 'preview'} />}
                                {preview.type === 'video' && (
                                    <video controls src={preview.url} autoPlay playsInline />
                                )}
                                {preview.type === 'audio' && (
                                    <FullScreenAudioPlayer key={preview.url} src={preview.url} />
                                )}
                            </div>

                            <div className="aw-lightbox-footer">
                                <a href={preview.url} target="_blank" rel="noreferrer" className="aw-open-original">Open original</a>
                                <button className="aw-lightbox-close-2" onClick={() => setPreview(null)}>Close</button>
                            </div>
                        </div>
                    </div>
                )
            }

            {
                ctxMenu.show && (
                    <div
                        className="aw-ctx-menu"
                        style={{
                            position: 'fixed',
                            top: ctxMenu.y,
                            left: ctxMenu.x,
                            zIndex: 4000,
                            background: 'rgba(24,24,24,0.98)',
                            border: '0.1vh solid #333',
                            borderRadius: '0.6vh',
                            padding: '0.4vh',
                            minWidth: '12vh',
                            boxShadow: '0 0.8vh 2.4vh rgba(0,0,0,0.45)'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            className="aw-ctx-item"
                            onClick={handleDownloadFromMenu}
                        >
                            Download
                        </button>
                        <button
                            className="aw-ctx-item"
                            onClick={handleDeleteFromMenu}
                        >
                            Delete
                        </button>
                    </div>
                )
            }

            <input
                id="aw-hidden-file-input"
                type="file"
                accept="image/*"
                style={{ display: 'none' }}
                onChange={(e) => {
                    handleFile(e.target.files?.[0]);
                    e.target.value = '';
                }}
            />
        </div >
    );
}
